import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, EmptyState, Spinner } from "@cntrl/ui";
import { onKeyboardToggle, setStatusBarStyle } from "@cntrl/native";
import { useMe, useNavigation } from "../../app/hooks/useSession.js";
import { useResolvedActiveBandId } from "../../app/activeBand.js";
import { BandProvider, bandContextFromNav, isProContext } from "../../app/bandContext.js";
import { useLiveGroup } from "../../app/realtime/RealtimeProvider.js";
import { isFullScreenRoute, isDosTabRoute } from "./mobileTabs.js";
import { MobileHeader } from "./MobileHeader.js";
import { MobileTabBar } from "./MobileTabBar.js";
import { MoreSheet } from "./MoreSheet.js";
import { FixtureSwitchDialog } from "../../features/dev/FixtureSwitchDialog.js";
import { usePullToRefresh } from "./usePullToRefresh.js";
import styles from "./MobileShell.module.scss";

/** The theme-color the mobile shell paints the status-bar/browser-chrome area with. In light theme the top bar
 *  is solid white to the top edge (ART-176), so the status-bar region matches it — white, not the #efefee canvas
 *  (the content below the bar stays --ac-color-bg). Dark uses its own ground below. */
const CANVAS_THEME_COLOR = "#ffffff";
/** The dark-theme counterpart (DOS on mobile, or a dev dark preference). Literal, mirroring the light one —
 *  the token registry is theme-unaware in JS, so the dark ground (--ac-color-bg under [data-theme=dark]) is
 *  hardcoded here the same way the light value is. */
const DARK_THEME_COLOR = "#0e0c0b"; // --ac-color-bg (dark)

/** Ensure a `<meta name="theme-color">` exists and set it (per-route theme-color).*/
function applyThemeColor(color: string) {
  if (typeof document === "undefined") return;
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    document.head.appendChild(meta);
  }
  meta.content = color;
}

export interface MobileShellProps {
  children: React.ReactNode;
  onLogout: () => void;
  onNavigate?: (route: string) => void;
  /** The current pathname (from the router shell) — drives the active-tab highlight + the per-route status bar. */
  activeRoute: string;
  /** The resolved theme for this scope (ShellLayout owns the rule: dev preference, or DOS forced dark on
   *  mobile). Drives `data-theme` on the shell root + the native status-bar content colour and theme-color.
   *  Defaults light so existing callers/tests are unaffected. */
  theme?: "light" | "dark";
}

/**
 * MobileShell — the Capacitor/narrow-viewport chrome: a role-keyed 5-tab bottom bar with the lifted
 * center DOS button and a More sheet, both rendered from the SERVER navigation document (`nav.mobile`,
 * capabilities-as-data — the client holds NO role→tab matrix). Chrome only, zero feature logic: it wraps
 * the routed module in the same `BandContext` (`shell:'mobile'`) every other shell uses. Per-route StatusBar
 * style + theme-color, safe-area padding, pull-to-refresh, and keyboard-up-hides-the-tab-bar are its
 * device concerns; loading/error states are designed, not blank.
 */
export function MobileShell({ children, onLogout, onNavigate, activeRoute, theme = "light" }: MobileShellProps) {
  const me = useMe();
  // Remembered on this device → starred on the account → first band seat (ART-319; see activeBand.ts).
  const activeBandId = useResolvedActiveBandId();
  const context = activeBandId ? `band:${activeBandId}` : undefined;
  const nav = useNavigation(context);

  const bandContext = useMemo(
    () => (nav.data ? bandContextFromNav(nav.data, "mobile") : null),
    [nav.data],
  );

  const [moreOpen, setMoreOpen] = useState(false);
  // Stable so the memoized MobileTabBar does not re-render on every unrelated shell toggle.
  const openMore = useCallback(() => setMoreOpen(true), []);
  const closeMore = useCallback(() => setMoreOpen(false), []);
  const [keyboardUp, setKeyboardUp] = useState(false);
  // The DEV fixture switcher (ART-168). The button lives in MobileHeader (chrome, always on screen — which is
  // where the acting-as glow marker has to be), but the dialog is mounted HERE at shell root: `Modal` renders
  // in place rather than through a portal, and the header is a stacking context (`backdrop-filter`), so a
  // dialog opened from inside the header would be trapped in the nav bar — the exact reason ArtistShell mounts
  // it at its own root. Renders nothing outside a non-production web lane / non-fixture account (the button
  // gates itself), so this is inert on native and in production.
  const [fixtureSwitchOpen, setFixtureSwitchOpen] = useState(false);

  // The shell's realtime groups (the ArtistShell contract): the person group (notifications/badges) + the
  // active band group. personId is latched so a band switch's transient /me eviction never churns it.
  const [stablePersonId, setStablePersonId] = useState<string>();
  useEffect(() => {
    const id = me.data?.person?.personId;
    if (id) setStablePersonId(id);
  }, [me.data]);
  useLiveGroup(stablePersonId ? `user:${stablePersonId}` : null);
  useLiveGroup(activeBandId ? `band:${activeBandId}` : null);


  // Keyboard-up hides the tab bar. Native-only seam; no-op on web.
  useEffect(() => onKeyboardToggle(setKeyboardUp), []);

  // Per-route StatusBar content style + theme-color, keyed on the resolved THEME. A dark scope (DOS on mobile,
  // or a dev dark preference) takes LIGHT status-bar content over the dark ground; light takes dark content
  // over the light canvas — the "immersive dark screens opt into light content + their own theme-color" the
  // seam always anticipated. NB setStatusBarStyle's argument is the CONTENT colour, not the background.
  useEffect(() => {
    const dark = theme === "dark";
    setStatusBarStyle(dark ? "light" : "dark");
    applyThemeColor(dark ? DARK_THEME_COLOR : CANVAS_THEME_COLOR);
  }, [theme]);

  const pull = usePullToRefresh(async () => {
    await Promise.all([me.refetch(), nav.refetch()]);
  });

  if (me.isError) {
    return (
      <div className={styles.screen}>
        <div className={styles.content}>
          <EmptyState
            title="We couldn't load your account"
            description="Check your connection and try again."
            action={<Button onClick={() => me.refetch()}>Retry</Button>}
          />
        </div>
      </div>
    );
  }

  const tabs = nav.data?.mobile?.tabs ?? [];
  const moreRows = nav.data?.mobile?.more ?? [];

  // AN OPEN CONVERSATION TAKES THE WHOLE SCREEN (ART-159, C-Mobile) — no app header, no tab bar. It draws
  // its own sticky header with the chat's name, its tags and a back arrow, so the app's would be a second
  // title and a second back affordance on one screen; and the composer is the bottom of the screen, which
  // is where the tab bar otherwise is. The shell decides this from the ROUTE rather than the screen asking
  // for it: a routed child switching its parent's chrome off is coupling that survives until two of them
  // want it and disagree.
  const takeover = isFullScreenRoute(activeRoute);
  // DOS kills the shell's top bar and draws its own (back + details + schedule), but KEEPS the tab bar.
  const dosHeaderless = isDosTabRoute(activeRoute);

  return (
    // `data-theme` here themes the visible shell (header + content + tab bar) — the whole DOS screen goes dark
    // on mobile. ShellLayout also sets it on <html> so portalled overlays inherit; this is belt-and-braces and
    // what component tests assert against.
    <div className={styles.screen} data-theme={theme}>
      {!takeover && !dosHeaderless && (
        <MobileHeader
          activeRoute={activeRoute}
          activeBandId={activeBandId}
          isPro={isProContext(bandContext)}
          onNavigate={onNavigate}
          onLogout={onLogout}
          fixtureSwitchOpen={fixtureSwitchOpen}
          onOpenFixtureSwitch={() => setFixtureSwitchOpen(true)}
        />
      )}
      <main
        className={styles.content}
        data-headerless={dosHeaderless || undefined}
        onTouchStart={pull.handlers.onTouchStart}
        onTouchMove={pull.handlers.onTouchMove}
        onTouchEnd={pull.handlers.onTouchEnd}
      >
        {(pull.distance > 0 || pull.refreshing) && (
          <div
            className={styles.pullIndicator}
            style={{ height: pull.refreshing ? 44 : pull.distance }}
            role="status"
            aria-live="polite"
          >
            <Spinner size={18} label={pull.refreshing ? "Refreshing…" : "Pull to refresh"} />
          </div>
        )}
        {bandContext ? (
          // A band-scoped module ALWAYS mounts under a resolved BandProvider — never bare.
          <BandProvider value={bandContext}>{children}</BandProvider>
        ) : activeBandId && nav.isError ? (
          <EmptyState
            title="We couldn't load this band"
            description="Check your connection and try again."
            action={<Button onClick={() => nav.refetch()}>Retry</Button>}
          />
        ) : me.isLoading || activeBandId ? (
          <div className={styles.contentLoading} role="status" aria-live="polite">
            <Spinner size={22} label="Loading your band…" />
          </div>
        ) : (
          // Session resolved with no band (org-home): the module decides (useBandContextOrNull).
          children
        )}
      </main>

      {tabs.length > 0 && !takeover && (
        <MobileTabBar
          tabs={tabs}
          activeRoute={activeRoute}
          onNavigate={onNavigate}
          onOpenMore={openMore}
          hidden={keyboardUp}
        />
      )}

      {/* Kept MOUNTED and toggled (not `{moreOpen && …}`) so the sheet is warm — opening is a CSS state
          flip with no mount cost. Its rows are already in `nav.mobile.more`, so warming costs nothing. */}
      <MoreSheet rows={moreRows} open={moreOpen} onNavigate={onNavigate} onClose={closeMore} />

      {/* At shell root, not inside the header — see the fixtureSwitchOpen note above. */}
      {fixtureSwitchOpen && (
        <FixtureSwitchDialog
          onClose={() => setFixtureSwitchOpen(false)}
          onNavigate={(to) => onNavigate?.(to)}
        />
      )}
    </div>
  );
}
